/**
 * سربرگِ «کاربران» — WHOSUP-243، مایگریشن 0149.
 *
 * ═══ چرا کنارِ «فعالیتِ کاربران» و نه داخلش ═══
 *
 * آن سربرگ **تجمیع** است: می‌گوید ده روزِ گذشته ۱۲ نفر آمدند و پرتکرارترین
 * رویداد کدام بود. این یکی **ردیف** است: کدام ۱۲ نفر، هر کدام چقدر، و
 * آخرین بار کِی. دو سوالِ متفاوت با دو ریتمِ متفاوت — همان دلیلی که «کرالر»
 * و «منابع» هم دو سربرگ‌اند.
 *
 * ═══ کارِ دومِ این صفحه: علامتِ «تستی» ═══
 *
 * مایگریشنِ 0119 حساب‌های داخلی را از آمار بیرون گذاشت، ولی افزودنِ حسابِ
 * تازه به آن فهرست فقط با `service_role` و از خطِ فرمان ممکن بود. نتیجه‌اش
 * این شد که عملاً هیچ‌وقت انجام نشد. دکمه‌ی همین جدول همان کار را می‌کند.
 *
 * ⚠ **حسابی که ایمیلش الگو دارد با دکمه برنمی‌گردد** و دکمه‌اش هم غیرفعال
 * است: آن حساب به‌خاطرِ `is_internal_email` داخلی است و نه به‌خاطرِ جدولِ
 * استثناها. دکمه‌ای که کاری نکند ولی فعال به نظر برسد، بدتر از نبودنش است.
 *
 * ⚠ **هیچ چیزی حذف نمی‌شود.** علامت یعنی «نشمار»، نه «پاک کن» — همان
 * قاعده‌ی 0119. برگرداندنش هم یک کلیک است.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { reportError } from '@/core/lib/report';
import {
  ACTIVE_EVENTS_D10,
  DORMANT_DAYS,
  LEVELS,
  LEVEL_LABEL,
  buildUsersReport,
  type UserLevel,
  type UserRow,
} from '@/core/lib/usersReport';
import { PairLegend, Two, fmtNum as fmt } from '@/ui/Num';
import { Hint } from '@/ui/Hint';

/** ستونِ دومِ این سربرگ — مثلِ «فعالیتِ کاربران» اجرا ندارد. */
const DAY = '۲۴ ساعت';

/** کدام حساب‌ها در جدول بیایند. */
type Show = 'all' | 'real' | 'internal';

const SHOW_LABEL: Record<Show, string> = {
  all: 'همه',
  real: 'واقعی',
  internal: 'داخلی/تستی',
};

const LEVEL_HELP: Record<UserLevel, string> = {
  active: `در ده روزِ گذشته دستِ‌کم ${ACTIVE_EVENTS_D10} رویداد داشته و تازه هم دیده شده`,
  light: 'تازه دیده شده ولی کم — زیرِ آستانه‌ی «فعال»',
  dormant: `بیشتر از ${DORMANT_DAYS} روز است که دیده نشده`,
  never: 'هیچ رویدادی، هیچ سیگنالی و هیچ پاسخی ندارد — ثبت‌نام کرد و رفت',
};

/** تاریخِ لاتین در سندِ راست‌به‌چپ باید جزیره‌ی خودش باشد. */
function When({ iso, days }: { iso: string | null; days: number | null }) {
  if (!iso) return <span className="dim">—</span>;
  return (
    <span className="dim ltr" title={new Date(iso).toLocaleString('en-CA')}>
      {new Date(iso).toLocaleDateString('en-CA')}
      {days != null ? <span className="dim">{` (${fmt(days)}d)`}</span> : null}
    </span>
  );
}

function LevelChip({ level }: { level: UserLevel }) {
  const cls = level === 'active' ? 'chip on' : level === 'never' ? 'chip warn' : 'chip';
  return <span className={cls} title={LEVEL_HELP[level]}>{LEVEL_LABEL[level]}</span>;
}

/**
 * ستونِ اولِ هر ردیف: تیکِ «تستی».
 *
 * ⚠ اولین شکلش دکمه بود («تستی» / «برگردان به آمار») و **پیدا نشد**: کسی
 * که فهرستِ آدم‌ها را نگاه می‌کند دنبالِ تیکِ کنارِ اسم می‌گردد، نه دنبالِ
 * دکمه‌ی ته ردیف. جایش هم عوض شد: اولِ ردیف و کنارِ نام، نه آخرش.
 *
 * سه حالت دارد و هر سه باید از هم قابلِ تشخیص باشند:
 *   تیکِ باز       حسابِ عادی — می‌شود علامتش زد.
 *   تیکِ خورده     دستی علامت خورده — با همان تیک برمی‌گردد.
 *   تیکِ قفل       به‌خاطرِ الگوی ایمیلش داخلی است و با تیک برنمی‌گردد،
 *                  پس غیرفعال است و زیرش می‌نویسد چرا. تیکی که کار نکند و
 *                  دلیلش را هم نگوید، از نبودنش بدتر است.
 */
function MarkCell({ r, busy, onSet }: {
  r: UserRow; busy: boolean; onSet: (internal: boolean) => void;
}) {
  const locked = r.internal && !r.canUnmark;
  const why = locked
    ? (r.internal_source === 'email'
      ? 'ایمیلش با الگوهای is_internal_email می‌خواند (مایگریشن 0119)، پس با تیک برنمی‌گردد'
      : 'از راهی جز الگوی ایمیل و جدولِ استثناها داخلی شده، پس با تیک برنمی‌گردد')
    : r.internal
      ? `${r.internal_reason || 'دستی علامت خورده'} — تیک را بردار تا دوباره در آمارها شمرده شود`
      : 'تیک بزن تا از آمارِ رفتارِ کاربران بیرون برود. هیچ داده‌ای حذف نمی‌شود.';

  return (
    <label className="markcell" title={why}>
      <input type="checkbox" checked={r.internal} disabled={locked || busy}
        onChange={(e) => onSet(e.target.checked)} />
      {locked ? (
        <span className="dim">{r.internal_source === 'email' ? 'ایمیل' : 'خودکار'}</span>
      ) : null}
    </label>
  );
}

export function UsersTab() {
  const qc = useQueryClient();
  /** متنِ داخلِ کادر، و آن‌چه واقعاً به دیتابیس رفته. دومی کلیدِ کوئری است. */
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [show, setShow] = useState<Show>('all');
  const [level, setLevel] = useState<UserLevel | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['users', q],
    queryFn: () => opsRepo.users(q || null),
  });

  const mark = useMutation({
    mutationFn: (v: { id: string; internal: boolean }) =>
      opsRepo.setInternal(v.id, v.internal),
    onSuccess: (_r, v) => {
      setErr(null);
      setDone(v.internal
        ? 'علامت خورد — از این به بعد در آمارِ فعالیت و سفرِ کاربر شمرده نمی‌شود. هیچ داده‌ای حذف نشد.'
        : 'برگشت به آمار.');
      // آمارِ آن دو سربرگ همین حالا عوض شد؛ کشِ کهنه یعنی دو عددِ متناقض
      // روی یک داشبورد.
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['activity'] });
      qc.invalidateQueries({ queryKey: ['journeys'] });
    },
    onError: (e: unknown) => {
      setDone(null);
      setErr(e instanceof Error ? e.message : String(e));
      reportError('admin.setInternal', e);
    },
  });

  const rep = useMemo(
    () => (list.data ? buildUsersReport(list.data) : null),
    [list.data],
  );

  /**
   * ⚠ فیلترِ «همه/واقعی/داخلی» قبل از شمارشِ چیپ‌های حکم اعمال می‌شود و نه
   * بعدش. اگر چیپ همیشه عددِ خلاصه را می‌نوشت (که عمداً فقط حساب‌های
   * واقعی را می‌شمارد)، روی نمای «داخلی/تستی» چیپ «فعال ۰» می‌گفت در حالی
   * که جدولِ زیرش چند ردیفِ فعال داشت — عددی که به چیزی که زیرش است
   * نمی‌خورد.
   */
  const scoped = useMemo(() => {
    if (!rep) return [];
    return rep.rows.filter((r) => {
      if (show === 'real' && r.internal) return false;
      if (show === 'internal' && !r.internal) return false;
      return true;
    });
  }, [rep, show]);

  const levelCount = useMemo(() => {
    const n: Record<UserLevel, number> = { active: 0, light: 0, dormant: 0, never: 0 };
    for (const r of scoped) n[r.level] += 1;
    return n;
  }, [scoped]);

  const rows = useMemo(
    () => (level ? scoped.filter((r) => r.level === level) : scoped),
    [scoped, level],
  );

  if (list.isLoading) return <p className="dim">…</p>;
  if (list.error) return <div className="card"><p className="err">{String(list.error)}</p></div>;
  if (!rep) return null;

  const s = rep.summary;

  return (
    <>
      <PairLegend runLabel={DAY}
        note="ستونِ «رویداد» اجرا ندارد، پس ستونِ دومش ۲۴ ساعتِ اخیر است." />

      <p className="note">
        <b>تیکِ ستونِ اول</b> حساب را «تستی» می‌کند، یعنی در آمارِ رفتارِ
        کاربران شمرده نشود — امروز دو جا: سربرگِ «فعالیتِ کاربران» و بلوکِ
        «سفرِ کاربر» (مایگریشن‌های 0119 و 0120). هیچ داده‌ای حذف نمی‌شود و
        برداشتنِ تیک همان لحظه برش می‌گرداند. حسابی که ایمیلش با الگوهای{' '}
        <span className="mono">is_internal_email</span> می‌خواند از اول داخلی
        است و تیکش قفل — کنارش نوشته «ایمیل».
      </p>

      <div className="grid n4" style={{ marginTop: 12 }}>
        <div className="card stat">
          <div className="n">{fmt(s.total)}</div>
          <div className="k">کلِ حساب‌ها</div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(s.real)}</div>
          <div className="k">
            <Hint text="واقعی" help="کل، منهای حساب‌های داخلی/تستی — همان‌هایی که در آمار می‌آیند" />
          </div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(s.internal)}</div>
          <div className="k">
            <Hint text="داخلی/تستی" help="حساب‌های خودمان و تستی — از آمارِ رفتارِ کاربران بیرون‌اند" />
          </div>
        </div>
        <div className="card stat good">
          <div className="n">{fmt(s.levels.active)}</div>
          <div className="k">
            <Hint text="فعالِ واقعی" help={LEVEL_HELP.active} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="filters" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
          <input className="input" placeholder="نام، نامِ کاربری، ایمیل یا شماره" value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setQ(draft.trim()); }} />
          <button type="button" className="btn" onClick={() => setQ(draft.trim())}>جست‌وجو</button>
          {q ? (
            <button type="button" className="btn ghost"
              onClick={() => { setDraft(''); setQ(''); }}>پاک کن</button>
          ) : null}

          <span style={{ width: 10 }} />
          {(Object.keys(SHOW_LABEL) as Show[]).map((k) => (
            <button key={k} type="button" className={`chip${show === k ? ' on' : ''}`}
              onClick={() => setShow(k)}>{SHOW_LABEL[k]}</button>
          ))}

          <span style={{ width: 10 }} />
          {LEVELS.map((l) => (
            <button key={l} type="button" className={`chip${level === l ? ' on' : ''}`}
              title={`${LEVEL_HELP[l]} — عدد روی همین نمای «${SHOW_LABEL[show]}» شمرده شده`}
              onClick={() => setLevel(level === l ? null : l)}>
              {LEVEL_LABEL[l]} <b>{fmt(levelCount[l])}</b>
            </button>
          ))}

          <span className="spacer" style={{ flex: 1 }} />
          <span className="dim">{fmt(rows.length)} ردیف</span>
        </div>

        {s.capped ? (
          <p className="note">
            {fmt(s.matched)} حساب با این جست‌وجو خواند ولی فقط {fmt(s.shown)} تا
            آمد — باریک‌ترش کن، وگرنه بقیه دیده نمی‌شوند.
          </p>
        ) : null}

        {err ? <p className="err" style={{ marginBottom: 8 }}>{err}</p> : null}
        {done ? <p className="dim" style={{ marginBottom: 8 }}>{done}</p> : null}

        {rows.length === 0 ? (
          <p className="dim">
            {s.total === 0 ? 'هنوز هیچ حسابی ساخته نشده.' : 'با این فیلترها چیزی نماند.'}
          </p>
        ) : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>
                    <Hint text="تستی"
                      help="تیک یعنی این حساب در آمارِ رفتارِ کاربران شمرده نشود. هیچ داده‌ای حذف نمی‌شود." />
                  </th>
                  <th>کاربر</th>
                  <th>ایمیل</th>
                  <th>
                    <Hint text="موبایل"
                      help="بهترین شماره‌ای که داریم. اگر فقط ۱۰ رقم است یعنی همان phone_key است و نه شماره‌ی کامل." />
                  </th>
                  <th>وضعیت</th>
                  <th className="num">رویداد</th>
                  <th className="num">نشست</th>
                  <th className="num">
                    <Hint text="سیگنال" help="سیگنال‌هایی که خودش ساخته — روی هم" />
                  </th>
                  <th className="num">
                    <Hint text="پاسخ" help="دعوت‌هایی که رویشان تصمیم گرفته (بی‌جوابْ شمرده نمی‌شود)" />
                  </th>
                  <th className="num">دوست</th>
                  <th>آخرین بار</th>
                  <th>عضویت</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={r.internal ? { opacity: 0.62 } : undefined}>
                    <td>
                      <MarkCell r={r}
                        busy={mark.isPending && mark.variables?.id === r.id}
                        onSet={(internal) => { setDone(null); mark.mutate({ id: r.id, internal }); }} />
                    </td>
                    <td>
                      {r.display_name}
                      <div className="dim mono">@{r.username}</div>
                      {r.is_admin ? <span className="chip">ادمین</span> : null}
                    </td>
                    <td className="mono">{r.email ?? '—'}</td>
                    <td className="mono">{r.phone ?? '—'}</td>
                    <td><LevelChip level={r.level} /></td>
                    <td className="num"><Two v={r.events} runLabel={DAY} /></td>
                    <td className="num">{fmt(r.sessions)}</td>
                    <td className="num">{fmt(r.signals)}</td>
                    <td className="num">{fmt(r.responses)}</td>
                    <td className="num">{fmt(r.friends)}</td>
                    <td><When iso={r.last_seen} days={r.lastSeenDays} /></td>
                    <td><When iso={r.created_at} days={r.ageDays} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
